import { useState, useEffect } from "react";
import type {
  InvestmentPlan,
  InvestmentPlans,
  InvestmentPlanType,
  InvestmentEffectLevel,
} from "../sim/investment-plans";
import {
  INVESTMENT_PRESETS,
  createDefaultPlan,
  getPlanAnnualKWh,
  estimatePlanSavingsSek,
  calculatePlanPayback,
} from "../sim/investment-plans";

interface InvestmentPlansPanelProps {
  plans: InvestmentPlans;
  onChange: (plans: InvestmentPlans) => void;
  /** Genomsnittligt importpris kr/kWh för payback-beräkning */
  avgImportPricePerKWh: number;
  /** Kontext för dynamiska presets baserat på användarens förbrukning */
  importContext: {
    annualImportKWh: number;
    winterImportKWh: number;
    eveningImportKWh: number;
  };
}

export default function InvestmentPlansPanel({
  plans,
  onChange,
  avgImportPricePerKWh,
  importContext,
}: InvestmentPlansPanelProps) {
  const [expanded, setExpanded] = useState(plans.enabled);

  function toggleEnabled() {
    const next = !plans.enabled;
    setExpanded(next);
    onChange({ ...plans, enabled: next });
  }

  function addPlan(type: InvestmentPlanType) {
    const newPlan = createDefaultPlan(type);
    onChange({ ...plans, plans: [...plans.plans, newPlan] });
  }

  function removePlan(id: string) {
    onChange({ ...plans, plans: plans.plans.filter((p) => p.id !== id) });
  }

  function updatePlan(id: string, updates: Partial<InvestmentPlan>) {
    onChange({
      ...plans,
      plans: plans.plans.map((p) => (p.id === id ? { ...p, ...updates } : p)),
    });
  }

  const existingTypes = new Set(plans.plans.map((p) => p.type));
  const availableTypes: InvestmentPlanType[] = (
    ["insulation", "supplementary_heating", "ev"] as InvestmentPlanType[]
  ).filter((t) => !existingTypes.has(t));

  return (
    <div className="investment-plans-panel">
      <button
        type="button"
        className="investment-plans-toggle"
        onClick={toggleEnabled}
        aria-expanded={expanded}
      >
        <span className="investment-plans-toggle-icon">
          {expanded ? "−" : "+"}
        </span>
        <span className="investment-plans-toggle-text">
          <strong>Planerar du fler elinvesteringar?</strong>
          <small>
            Se hur isolering, kompletterande värme eller elbil påverkar
            batteriets återbetalningstid.
          </small>
        </span>
      </button>

      {expanded && (
        <div className="investment-plans-content">
          <p className="investment-plans-intro">
            Aktivera en eller flera planerade åtgärder för att modellera
            hur de påverkar batteriets ekonomi. Effektnivåerna är ungefärliga
            spann. Verklig besparing kräver detaljerad energiberäkning.
          </p>

          {plans.plans.length === 0 && (
            <p className="investment-plans-empty">
              Inga åtgärder tillagda än. Lägg till nedan.
            </p>
          )}

          {plans.plans.map((plan) => (
            <PlanEditor
              key={plan.id}
              plan={plan}
              avgImportPricePerKWh={avgImportPricePerKWh}
              importContext={importContext}
              onUpdate={(updates) => updatePlan(plan.id, updates)}
              onRemove={() => removePlan(plan.id)}
            />
          ))}

          {availableTypes.length > 0 && (
            <div className="investment-plans-add">
              <span className="investment-plans-add-label">
                Lägg till åtgärd:
              </span>
              <div className="investment-plans-add-buttons">
                {availableTypes.map((type) => (
                  <button
                    key={type}
                    type="button"
                    className="investment-plans-add-button"
                    onClick={() => addPlan(type)}
                  >
                    + {INVESTMENT_PRESETS[type].label}
                  </button>
                ))}
              </div>
            </div>
          )}

          <p className="investment-plans-disclaimer">
            <strong>Notera:</strong> Detta är förenklade bedömningar, inte
            exakta beräkningar. Isolering antas ge proportionell reduktion
            under uppvärmningssäsongen, kamin antas ersätta el på kvällar,
            elbil antas laddas smart på nattimmar.
          </p>
        </div>
      )}
    </div>
  );
}

interface PlanEditorProps {
  plan: InvestmentPlan;
  avgImportPricePerKWh: number;
  importContext: {
    annualImportKWh: number;
    winterImportKWh: number;
    eveningImportKWh: number;
  };
  onUpdate: (updates: Partial<InvestmentPlan>) => void;
  onRemove: () => void;
}

function PlanEditor({
  plan,
  avgImportPricePerKWh,
  importContext,
  onUpdate,
  onRemove,
}: PlanEditorProps) {
  const preset = INVESTMENT_PRESETS[plan.type];
  const annualKWh = getPlanAnnualKWh(plan, importContext);
  const annualSavings = estimatePlanSavingsSek(
    plan,
    avgImportPricePerKWh,
    importContext,
  );
  const payback = calculatePlanPayback(
    plan,
    avgImportPricePerKWh,
    importContext,
  );

  const isEV = plan.type === "ev";

  // Lokal string state för investeringskostnad — fixar buggen där man inte
  // kan radera sista siffran. type="number" + controlled value + Number() || 0
  // betyder att tom sträng blir 0, vilket stör inputtillståndet.
  const [costStr, setCostStr] = useState(plan.investmentCostSek.toString());
  useEffect(() => {
    // Synca om planen uppdateras externt
    if (parseInt(costStr || "0", 10) !== plan.investmentCostSek) {
      setCostStr(plan.investmentCostSek.toString());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan.investmentCostSek]);

  // Räkna ut dynamiska kWh-värden för visning per nivå
  function levelKWh(level: InvestmentEffectLevel): number {
    return Math.abs(
      getPlanAnnualKWh({ ...plan, effectLevel: level }, importContext),
    );
  }

  return (
    <div className={`plan-editor ${plan.enabled ? "" : "is-disabled"}`}>
      <div className="plan-editor-header">
        <label className="plan-editor-toggle">
          <input
            type="checkbox"
            checked={plan.enabled}
            onChange={(e) => onUpdate({ enabled: e.target.checked })}
          />
          <strong>{preset.label}</strong>
        </label>
        <button
          type="button"
          className="plan-editor-remove"
          onClick={onRemove}
          aria-label={`Ta bort ${preset.label}`}
        >
          Ta bort
        </button>
      </div>

      <p className="plan-editor-help">{preset.helpText}</p>

      <div className="plan-editor-row">
        <span className="plan-editor-label">Omfattning:</span>
        <div className="plan-editor-levels">
          {(["conservative", "neutral", "optimistic"] as InvestmentEffectLevel[]).map(
            (level) => (
              <label
                key={level}
                className={`plan-editor-level ${
                  plan.effectLevel === level ? "is-selected" : ""
                }`}
              >
                <input
                  type="radio"
                  name={`effect-${plan.id}`}
                  value={level}
                  checked={plan.effectLevel === level}
                  onChange={() => onUpdate({ effectLevel: level })}
                />
                <span className="plan-editor-level-label">
                  {level === "conservative"
                    ? "Försiktig"
                    : level === "neutral"
                    ? "Neutral"
                    : "Optimistisk"}
                </span>
                <small>{preset.levels[level].description}</small>
                <small className="plan-editor-level-kwh">
                  {levelKWh(level).toLocaleString("sv-SE")} kWh/år
                </small>
              </label>
            ),
          )}
        </div>
      </div>

      {!isEV && (
        <div className="plan-editor-row">
          <label className="plan-editor-label" htmlFor={`cost-${plan.id}`}>
            Investeringskostnad:
          </label>
          <div className="plan-editor-cost">
            <input
              id={`cost-${plan.id}`}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={costStr}
              onChange={(e) => {
                // Tillåt bara siffror, ta bort allt annat
                const cleaned = e.target.value.replace(/\D/g, "");
                setCostStr(cleaned);
                onUpdate({
                  investmentCostSek: cleaned === "" ? 0 : parseInt(cleaned, 10),
                });
              }}
              onBlur={() => {
                // På blur: säkerställ att fältet inte är tomt
                if (costStr === "") setCostStr("0");
              }}
            />
            <span>kr</span>
          </div>
        </div>
      )}

      {plan.enabled && (
        <div className="plan-editor-summary">
          <div className="plan-editor-summary-item">
            <span className="plan-editor-summary-label">
              {isEV ? "Tillägg" : "Minskning"}
            </span>
            <strong>
              {Math.abs(annualKWh).toLocaleString("sv-SE")} kWh/år
            </strong>
          </div>
          <div className="plan-editor-summary-item">
            <span className="plan-editor-summary-label">
              {isEV ? "Extra elkostnad" : "Sparad elkostnad"}
            </span>
            <strong>
              {Math.abs(Math.round(annualSavings)).toLocaleString("sv-SE")} kr/år
            </strong>
          </div>
          {!isEV && plan.investmentCostSek > 0 && (
            <div className="plan-editor-summary-item">
              <span className="plan-editor-summary-label">Återbetalning</span>
              <strong>
                {payback === Infinity ? "—" : `${payback.toFixed(1)} år`}
              </strong>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
